/**
 * Where a site stands between "added" and "reporting". Shared by WorkspaceHub
 * (banner, listening notice, headline) and SitesGrid (row pill) so the two can
 * never disagree about a site.
 *
 * - `install`: the tag was never verified and nothing has arrived. Soft state:
 *   amber pill plus `TrackingNotInstalledBanner`, never a wall.
 * - `waiting`: the tag is verified but nothing has arrived yet. Grey
 *   "Listening" notice plus a "Waiting for first visit" pill. Clears on the
 *   first pageview like `install` does.
 * - `live`: the site has reported at least once.
 *
 * "Nothing has arrived" reads `stats.views/events` AND both 14-day windows.
 * `stats.*` are month to date, so the windows keep an established site with a
 * quiet month out of both soft states.
 */
export type SiteTrackingState = "install" | "waiting" | "live"

export interface TrackableSite {
  trackingStatus?: "not_installed" | "pending_verification" | "verified" | "error"
  stats?: { views?: number; events?: number }
  hasRecentViews?: boolean
  hasPreviousViews?: boolean
}

export function hasNoTraffic(site: TrackableSite): boolean {
  return (
    (site.stats?.views ?? 0) === 0 &&
    (site.stats?.events ?? 0) === 0 &&
    !site.hasRecentViews &&
    !site.hasPreviousViews
  )
}

export function siteTrackingState(site: TrackableSite): SiteTrackingState {
  if (!hasNoTraffic(site)) return "live"
  return site.trackingStatus === "verified" ? "waiting" : "install"
}

/**
 * Home headline and subline when every real site is verified and silent.
 * Returns null when any site is reporting or still needs the tag, so the
 * regular answer-first headline stands.
 */
export function waitingHeadline(
  states: Array<{ domain: string; state: SiteTrackingState }>,
  greeting: string,
): { headline: string; subline: string } | null {
  if (states.length === 0 || states.some((s) => s.state !== "waiting")) return null
  if (states.length === 1) {
    const domain = states[0].domain
    return {
      headline: `${domain} is connected. Waiting for its first visitor.`,
      subline: `${greeting} The tag on ${domain} is verified. Nothing has been recorded yet.`,
    }
  }
  return {
    headline: `Your ${states.length} sites are connected. Waiting for the first visitor.`,
    subline: `${greeting} The tag is verified on every site. Nothing has been recorded yet.`,
  }
}
